import invert from 'lodash/invert';
import identity from 'lodash/identity';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';

import {isCohortSegment, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {CohortsClause, GroupClause, QUERY_BUILDER_RESOURCE_TYPE_MAPPING} from 'common/report/insights/models/clause';
import {DATASET_MIXPANEL, ResourceType} from 'common/report/constants';
import {CohortsSection, GroupSection} from 'common/report/insights/models/section';
import {InsightsReportSectionsOverride} from 'common/report/insights/models/types';
import {Segmentation} from 'common/types/reports/query';
import {reporter} from 'common/report/error-reporting';
import {FILTER_VALUE_ATTRIBUTES} from 'common/widgets/property-filter-menu/models/property-filter';
import {isMultiAttributionSegmentation} from 'common/query-builder/query-entry-section/row-types/behavior/utils';

import {GroupClause as GroupClauseType, InsightsBookmarkSections} from './types';

/**
 * Transforms Cohorts and Group Sections into Segment
 * @param {bool} renameResourceTypes whether to rename "people" to "users", etc for resource types
 */
export function getSelectedSegments(
  cohortClauses: CohortsClause[],
  groupClauses: GroupClauseType[],
  renameResourceTypes = true,
): Segment[] {
  const convertGroupClause = (clause, forceGroupClause?: boolean) => {
    if (clause instanceof CohortsClause) {
      return {cohort: pick(clause, [`id`, `name`, `negated`, `raw_cohort`, `data_group_id`])};
    } else if (clause.clauses) {
      return clause.clauses.map((c) => convertGroupClause(c));
    } else if (forceGroupClause || clause instanceof GroupClause) {
      let resourceType = clause.resourceType;
      if (renameResourceTypes) {
        resourceType = invert(QUERY_BUILDER_RESOURCE_TYPE_MAPPING)[clause.resourceType] as ResourceType;
      }
      let basePropsKeys = [
        `behavior`,
        `tempDataGroupId`,
        `joinPropertyType`,
        `customBucket`,
        `customGroups`,
        `unit`,
        `dataGroupId`,
        `customProperty`,
        `customPropertyId`,
        `propertyObjectKey`,
        `listQuantifier`,
        `listItemGroup`,
      ];

      // Support for Value Comparison
      if (Object.prototype.hasOwnProperty.call(clause, `filterOperator`)) {
        basePropsKeys = basePropsKeys.concat(FILTER_VALUE_ATTRIBUTES);
      }
      const baseProps = pickBy(pick(clause, basePropsKeys), identity);

      // if group clause contains filterOperator, we treat it as a group_by clause that requires filter attributes
      // Need to override filterOperator and filterValue to render the query entry section correctly
      if (baseProps.filterOperator) {
        baseProps[`filterOperator`] = clause.propertyFilter.filterOperator;
        baseProps[`filterValue`] = clause.propertyFilter.filterValue;
      }

      return {
        [resourceType]: {
          ...baseProps,
          resourceType,
          propertyDefaultType: clause.propertyType,
          propertyName: clause.value,
          propertyType: clause.typeCast || clause[`filterType`] || clause.propertyType,
        },
      };
    } else {
      reporter.error(`Clause is neither of the type GroupClause or CohortsClause`);
      return null;
    }
  };

  return [
    ...(cohortClauses.length ? [cohortClauses.map((clause) => convertGroupClause(clause))] : []),
    ...groupClauses.map((clause) => convertGroupClause(clause)),
  ];
}

export function getSelectedSegmentsFromBookmark(bookmarkSections: InsightsBookmarkSections) {
  return getSelectedSegments(
    bookmarkSections.cohorts.map((cohort) => new CohortsClause(cohort)),
    bookmarkSections.group.map((group) => new GroupClause(group)),
  );
}

/**
 * Transforms Segments into Cohorts and Group Sections
 */
export function getSegmentSections(segments: Segment[]): [CohortsSection, GroupSection] {
  let cohortsClauses = [];
  const groupClauses = [];
  const multiAttributionClauses = [];

  segments.forEach((segment) => {
    // TODO: insights currently only supports a single segment of cohorts values
    if (isCohortSegment(segment)) {
      cohortsClauses = segment.map((item) => new CohortsClause({...item.cohort, dataset: DATASET_MIXPANEL}));
    } else {
      if (Array.isArray(segment)) {
        groupClauses.push(getSegmentSections(segment)[1]);
        return;
      }

      const groupBy: Segmentation = Object.values(segment)[0];

      const getClauseAttrs = (groupBy: Segmentation) => {
        const baseProps = pick(
          groupBy,
          `behavior`,
          `dataGroupId`,
          `customPropertyId`,
          `customProperty`,
          `joinPropertyType`,
          `tempDataGroupId`,
          `propertyObjectKey`,
          `unit`,
          `customBucket`,
          `customGroups`,
          `listQuantifier`,
          `listItemGroup`,
        );

        return {
          ...baseProps,
          ...(groupBy.propertyType !== groupBy.propertyDefaultType ? {typeCast: groupBy.propertyType} : {}),
          dataset: DATASET_MIXPANEL,
          dataGroupId: groupBy.dataGroupId,
          propertyType: groupBy.propertyDefaultType,
          resourceType: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[groupBy.resourceType],
          value: groupBy.propertyName,
        };
      };

      const groupClause = new GroupClause(getClauseAttrs(groupBy));
      if (isMultiAttributionSegmentation(groupBy)) {
        multiAttributionClauses.push(groupClause);
      } else {
        groupClauses.push(groupClause);
      }
    }
  });

  return [
    new CohortsSection(cohortsClauses),
    new GroupSection([
      ...groupClauses,
      // multi-attribution properties should always inner-most breakdowns
      ...multiAttributionClauses,
    ]),
  ];
}

export function getSegmentSectionsOverride(segments: Segment[]): InsightsReportSectionsOverride {
  const [cohortsSection, groupSection] = getSegmentSections(segments);
  return {
    cohorts: cohortsSection.toBookmarkData(),
    group: groupSection.toBookmarkData(),
  };
}
